import { useMemo, useRef, useState } from 'react'
import type { ComputedPiece } from '../../geometry/computeGarment'
import type { PathSegment } from '../../types/records'
import { categorizeSegment, type SegmentKind } from '../../geometry/segmentCategory'

interface Props {
  piece: ComputedPiece
}

interface PointXY {
  x: number
  y: number
}

export function PiecePreview({ piece }: Props) {
  const [showLabels, setShowLabels] = useState(true)
  const [showGrid, setShowGrid] = useState(true)
  const [showPoints, setShowPoints] = useState(true)
  const [showLengths, setShowLengths] = useState(false)
  const [showLegend, setShowLegend] = useState(false)
  const svgRef = useRef<SVGSVGElement | null>(null)

  const pointMap = useMemo(() => {
    const m = new Map<string, PointXY>()
    for (const p of piece.points) m.set(p.label, { x: p.x, y: p.y })
    return m
  }, [piece])

  const layout = useMemo(() => computeLayout(piece, pointMap), [piece, pointMap])

  if (!layout) {
    return <p className="muted">Nessun punto da visualizzare.</p>
  }

  const { minX, minY, widthCm, heightCm } = layout
  const viewBox = `${minX} ${minY} ${widthCm} ${heightCm}`

  // Scala punti/font in proporzione al bbox per avere una resa leggibile
  // a qualunque dimensione del pezzo
  const refSize = Math.max(widthCm, heightCm)
  const dotR = Math.max(0.25, refSize * 0.005)
  const labelFontSize = Math.max(1.6, refSize * 0.022)
  const labelOffset = dotR * 2.2

  const validSegments = piece.segments.filter(seg => {
    return pointMap.has(seg.from) && pointMap.has(seg.to)
  })

  const kindCounts = useMemo(() => {
    const c: Record<SegmentKind, number> = {
      outline: 0, dart: 0, fold: 0, reference: 0,
    }
    for (const seg of validSegments) c[categorizeSegment(seg.notes)]++
    return c
  }, [validSegments])

  return (
    <div className="piece-preview">
      <div className="piece-preview-toolbar">
        <label className="toggle-inline">
          <input type="checkbox" checked={showGrid} onChange={e => setShowGrid(e.target.checked)} />
          Griglia
        </label>
        <label className="toggle-inline">
          <input type="checkbox" checked={showPoints} onChange={e => setShowPoints(e.target.checked)} />
          Punti
        </label>
        <label className="toggle-inline">
          <input type="checkbox" checked={showLabels} onChange={e => setShowLabels(e.target.checked)} />
          Etichette
        </label>
        <label className="toggle-inline">
          <input type="checkbox" checked={showLengths} onChange={e => setShowLengths(e.target.checked)} />
          Misure
        </label>
        <label className="toggle-inline">
          <input type="checkbox" checked={showLegend} onChange={e => setShowLegend(e.target.checked)} />
          Legenda
        </label>
        <span className="piece-preview-size muted">
          {(layout.bboxWidthCm).toFixed(1)} × {(layout.bboxHeightCm).toFixed(1)} cm
        </span>
        <div className="piece-preview-actions">
          <button
            type="button"
            className="ghost-btn"
            onClick={() => downloadSvg(svgRef.current, layout, piece.italianLabel)}
            title="Scarica SVG con dimensioni reali in cm"
          >
            SVG
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => downloadPng(svgRef.current, layout, piece.italianLabel)}
            title="Scarica PNG (150 DPI)"
          >
            PNG
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => printSvg(svgRef.current, layout, piece.italianLabel)}
            title="Apri finestra di stampa pagina unica (per plotter)"
          >
            Stampa 1:1
          </button>
          <button
            type="button"
            className="ghost-btn"
            onClick={() => printSvgTiledA4(svgRef.current, layout, piece.italianLabel)}
            title="Stampa su più fogli A4 con segni di taglio (1:1 sulle pagine assemblate)"
          >
            Stampa A4
          </button>
        </div>
      </div>

      {showLegend && <Legend counts={kindCounts} />}

      <div className="piece-svg-wrap">
        <svg
          ref={svgRef}
          className="piece-svg"
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          xmlns="http://www.w3.org/2000/svg"
        >
          {showGrid && (
            <Grid minX={minX} minY={minY} widthCm={widthCm} heightCm={heightCm} />
          )}

          <g className="segments">
            {validSegments.map((seg, i) => (
              <SegmentRender key={i} seg={seg} pointMap={pointMap} />
            ))}
            {validSegments.length === 0 && piece.points.length >= 2 && (
              <FallbackHull points={piece.points} />
            )}
          </g>

          {showLengths && (
            <g className="segment-lengths">
              {validSegments.map((seg, i) => {
                const mid = segmentMidpoint(seg, pointMap)
                if (!mid) return null
                const len = segmentLength(seg, pointMap)
                if (len < 0.5) return null
                return (
                  <text
                    key={`len-${i}`}
                    x={mid.x}
                    y={mid.y}
                    className="segment-length"
                    style={{
                      fontSize: labelFontSize * 0.85,
                      strokeWidth: labelFontSize * 0.22,
                    }}
                  >
                    {len.toFixed(1)}
                  </text>
                )
              })}
            </g>
          )}

          {showPoints && (
            <g className="points">
              {piece.points.map(pt => (
                <g key={pt.label}>
                  <circle cx={pt.x} cy={pt.y} r={dotR} className="point-dot" />
                  {showLabels && (
                    <text
                      x={pt.x}
                      y={pt.y}
                      dx={labelOffset}
                      dy={-labelOffset}
                      className="point-label"
                      style={{ fontSize: labelFontSize, strokeWidth: labelFontSize * 0.25 }}
                    >
                      {pt.label}
                    </text>
                  )}
                </g>
              ))}
            </g>
          )}
        </svg>
      </div>
    </div>
  )
}

function computeLayout(piece: ComputedPiece, pointMap: Map<string, PointXY>) {
  const coords: PointXY[] = []
  for (const p of piece.points) coords.push({ x: p.x, y: p.y })
  for (const seg of piece.segments) {
    for (const v of seg.viaPoints) {
      const vp = pointMap.get(v)
      if (vp) coords.push(vp)
    }
  }
  if (coords.length === 0) return null

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const c of coords) {
    if (c.x < minX) minX = c.x
    if (c.y < minY) minY = c.y
    if (c.x > maxX) maxX = c.x
    if (c.y > maxY) maxY = c.y
  }

  const bboxWidthCm = maxX - minX
  const bboxHeightCm = maxY - minY
  const padding = Math.max(2, Math.max(bboxWidthCm, bboxHeightCm) * 0.06)

  return {
    minX: minX - padding,
    minY: minY - padding,
    widthCm: bboxWidthCm + padding * 2,
    heightCm: bboxHeightCm + padding * 2,
    bboxWidthCm,
    bboxHeightCm,
  }
}

function Grid({
  minX,
  minY,
  widthCm,
  heightCm,
}: {
  minX: number
  minY: number
  widthCm: number
  heightCm: number
}) {
  const step = 5
  const lines: { key: string; x1: number; y1: number; x2: number; y2: number; cls: string }[] = []
  const startX = Math.floor(minX / step) * step
  const startY = Math.floor(minY / step) * step
  for (let x = startX; x <= minX + widthCm; x += step) {
    lines.push({
      key: `vx${x}`,
      x1: x, y1: minY, x2: x, y2: minY + heightCm,
      cls: x === 0 ? 'grid-axis' : 'grid-line',
    })
  }
  for (let y = startY; y <= minY + heightCm; y += step) {
    lines.push({
      key: `hy${y}`,
      x1: minX, y1: y, x2: minX + widthCm, y2: y,
      cls: y === 0 ? 'grid-axis' : 'grid-line',
    })
  }
  return (
    <g className="grid">
      {lines.map(l => (
        <line key={l.key} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2} className={l.cls} />
      ))}
    </g>
  )
}

function classFor(seg: PathSegment, kind: SegmentKind): string {
  const base =
    seg.type === 'arc'
      ? 'segment-arc'
      : seg.type === 'curve'
        ? 'segment-curve'
        : 'segment-line'
  return `${base} kind-${kind}`
}

function SegmentRender({
  seg,
  pointMap,
}: {
  seg: PathSegment
  pointMap: Map<string, PointXY>
}) {
  const from = pointMap.get(seg.from)
  const to = pointMap.get(seg.to)
  if (!from || !to) return null

  const via: PointXY[] = []
  for (const v of seg.viaPoints) {
    const vp = pointMap.get(v)
    if (vp) via.push(vp)
  }

  const kind = categorizeSegment(seg.notes)
  const cls = classFor(seg, kind)
  const titleEl = seg.notes ? <title>{seg.notes}</title> : null

  if (seg.type === 'line' || via.length === 0) {
    return (
      <line
        x1={from.x} y1={from.y} x2={to.x} y2={to.y}
        className={cls}
      >
        {titleEl}
      </line>
    )
  }

  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    const d = `M ${from.x} ${from.y} Q ${cx} ${cy} ${to.x} ${to.y}`
    return (
      <path d={d} className={cls}>
        {titleEl}
      </path>
    )
  }

  const all = [from, ...via, to]
  const d = all.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  return (
    <path d={d} className={cls}>
      {titleEl}
    </path>
  )
}

function FallbackHull({ points }: { points: ComputedPiece['points'] }) {
  if (points.length < 2) return null
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ')
  return <path d={d} className="segment-fallback" />
}

const KIND_LABELS: Record<SegmentKind, { label: string; hint: string }> = {
  outline: {
    label: 'Cucitura / contorno',
    hint: 'Profili che vengono cuciti o tagliati: scollatura, fianco, spalla, giromanica, fondo…',
  },
  dart: {
    label: 'Ripresa / dardo',
    hint: 'Pieghe interne al pezzo che modellano la stoffa sul corpo.',
  },
  fold: {
    label: 'Centro / piegatura',
    hint: 'Asse di simmetria: il pezzo si specchia su questa linea.',
  },
  reference: {
    label: 'Linea di riferimento',
    hint: 'Linea di costruzione orizzontale (vita, bacino, torace…). Non si taglia.',
  },
}

function Legend({ counts }: { counts: Record<SegmentKind, number> }) {
  const items: SegmentKind[] = ['outline', 'dart', 'fold', 'reference']
  return (
    <div className="piece-legend">
      {items.map(k => {
        const n = counts[k]
        const dim = n === 0
        return (
          <div key={k} className={`legend-item ${dim ? 'dim' : ''}`} title={KIND_LABELS[k].hint}>
            <svg viewBox="0 0 30 6" className="legend-swatch" preserveAspectRatio="none">
              <line x1="0" y1="3" x2="30" y2="3" className={`segment-line kind-${k}`} />
            </svg>
            <span className="legend-label">{KIND_LABELS[k].label}</span>
            <span className="legend-count">{n}</span>
          </div>
        )
      })}
    </div>
  )
}

// Calcola la lunghezza in cm di un segmento (linea, curva o arco).
// Per curve/archi con un via point usa l'integrale numerico della Bezier
// quadratica passante per i 3 punti.
function segmentLength(seg: PathSegment, pointMap: Map<string, PointXY>): number {
  const from = pointMap.get(seg.from)
  const to = pointMap.get(seg.to)
  if (!from || !to) return 0

  const via: PointXY[] = []
  for (const v of seg.viaPoints) {
    const vp = pointMap.get(v)
    if (vp) via.push(vp)
  }

  if (seg.type === 'line' || via.length === 0) {
    return Math.hypot(to.x - from.x, to.y - from.y)
  }

  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    return quadraticBezierLength(from, { x: cx, y: cy }, to)
  }

  // Polilinea: somma dei segmenti rettilinei
  const all = [from, ...via, to]
  let total = 0
  for (let i = 0; i < all.length - 1; i++) {
    total += Math.hypot(all[i + 1].x - all[i].x, all[i + 1].y - all[i].y)
  }
  return total
}

function quadraticBezierLength(p0: PointXY, p1: PointXY, p2: PointXY): number {
  const N = 32
  let total = 0
  let prev = p0
  for (let i = 1; i <= N; i++) {
    const t = i / N
    const u = 1 - t
    const x = u * u * p0.x + 2 * u * t * p1.x + t * t * p2.x
    const y = u * u * p0.y + 2 * u * t * p1.y + t * t * p2.y
    total += Math.hypot(x - prev.x, y - prev.y)
    prev = { x, y }
  }
  return total
}

function segmentMidpoint(seg: PathSegment, pointMap: Map<string, PointXY>): PointXY | null {
  const from = pointMap.get(seg.from)
  const to = pointMap.get(seg.to)
  if (!from || !to) return null

  const via: PointXY[] = []
  for (const v of seg.viaPoints) {
    const vp = pointMap.get(v)
    if (vp) via.push(vp)
  }

  if (seg.type === 'line' || via.length === 0) {
    return { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 }
  }

  if (via.length === 1) {
    const v = via[0]
    const cx = 2 * v.x - 0.5 * from.x - 0.5 * to.x
    const cy = 2 * v.y - 0.5 * from.y - 0.5 * to.y
    return {
      x: 0.25 * from.x + 0.5 * cx + 0.25 * to.x,
      y: 0.25 * from.y + 0.5 * cy + 0.25 * to.y,
    }
  }

  return via[Math.floor(via.length / 2)]
}

interface Layout {
  minX: number
  minY: number
  widthCm: number
  heightCm: number
  bboxWidthCm: number
  bboxHeightCm: number
}

const EXPORT_SVG_STYLES = `
  .grid-line { stroke: rgba(0,0,0,0.06); stroke-width: 0.5; vector-effect: non-scaling-stroke; }
  .grid-axis { stroke: rgba(0,0,0,0.18); stroke-width: 0.9; vector-effect: non-scaling-stroke; }
  .segments line, .segments path { fill: none; stroke: black; stroke-width: 1.4; vector-effect: non-scaling-stroke; stroke-linecap: round; stroke-linejoin: round; }
  .segments .kind-dart { stroke: #b80056; stroke-dasharray: 3 2; stroke-width: 1.2; }
  .segments .kind-fold { stroke: #555; stroke-dasharray: 6 3; stroke-width: 1.0; }
  .segments .kind-reference { stroke: #888; stroke-width: 0.7; opacity: 0.8; }
  .segment-fallback { stroke: rgba(0,0,0,0.4); stroke-dasharray: 2 2; }
  .point-dot { fill: black; stroke: white; stroke-width: 1; vector-effect: non-scaling-stroke; }
  .point-label { fill: black; font-family: monospace; paint-order: stroke; stroke: white; stroke-linejoin: round; }
  .segment-length { fill: #555; font-family: monospace; text-anchor: middle; dominant-baseline: middle; paint-order: stroke; stroke: white; stroke-linejoin: round; }
`

// Serializza l'SVG corrente con dimensioni reali in cm (1:1).
function buildExportSvgString(svg: SVGSVGElement, layout: Layout): string {
  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('width', `${layout.widthCm}cm`)
  clone.setAttribute('height', `${layout.heightCm}cm`)
  clone.style.background = 'white'
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
  style.textContent = EXPORT_SVG_STYLES
  clone.insertBefore(style, clone.firstChild)
  return new XMLSerializer().serializeToString(clone)
}

// Serializza un tile dell'SVG: stesso contenuto ma viewBox/dimensioni
// limitati a un rettangolo del piano cm.
function buildTileSvgString(
  svg: SVGSVGElement,
  tileX: number,
  tileY: number,
  tileW: number,
  tileH: number,
): string {
  const clone = svg.cloneNode(true) as SVGSVGElement
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
  clone.setAttribute('viewBox', `${tileX} ${tileY} ${tileW} ${tileH}`)
  clone.setAttribute('width', `${tileW}cm`)
  clone.setAttribute('height', `${tileH}cm`)
  clone.setAttribute('preserveAspectRatio', 'xMinYMin meet')
  clone.style.background = 'white'
  const style = document.createElementNS('http://www.w3.org/2000/svg', 'style')
  style.textContent = EXPORT_SVG_STYLES
  clone.insertBefore(style, clone.firstChild)
  return new XMLSerializer().serializeToString(clone)
}

function sanitizeFilename(s: string): string {
  return s.replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').toLowerCase() || 'cartamodello'
}

function downloadSvg(svg: SVGSVGElement | null, layout: Layout, label: string) {
  if (!svg) return
  const svgString = buildExportSvgString(svg, layout)
  const blob = new Blob(['<?xml version="1.0" encoding="UTF-8"?>\n', svgString], {
    type: 'image/svg+xml',
  })
  triggerDownload(blob, `${sanitizeFilename(label)}.svg`)
}

async function downloadPng(svg: SVGSVGElement | null, layout: Layout, label: string, dpi = 150) {
  if (!svg) return
  const svgString = buildExportSvgString(svg, layout)
  const cmToPx = dpi / 2.54
  const widthPx = Math.max(1, Math.round(layout.widthCm * cmToPx))
  const heightPx = Math.max(1, Math.round(layout.heightCm * cmToPx))

  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
  const url = URL.createObjectURL(svgBlob)

  try {
    const img = new Image()
    img.src = url
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve()
      img.onerror = () => reject(new Error('Caricamento SVG fallito'))
    })

    const canvas = document.createElement('canvas')
    canvas.width = widthPx
    canvas.height = heightPx
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas context non disponibile')
    ctx.fillStyle = 'white'
    ctx.fillRect(0, 0, widthPx, heightPx)
    ctx.drawImage(img, 0, 0, widthPx, heightPx)

    const pngBlob = await new Promise<Blob | null>(resolve =>
      canvas.toBlob(b => resolve(b), 'image/png'),
    )
    if (!pngBlob) throw new Error('Conversione PNG fallita')
    triggerDownload(pngBlob, `${sanitizeFilename(label)}.png`)
  } catch (e) {
    alert(`Export PNG: ${e instanceof Error ? e.message : String(e)}`)
  } finally {
    URL.revokeObjectURL(url)
  }
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// Apre HTML in una nuova scheda via blob URL.
// Pattern necessario su Safari/iOS PWA: window.open('') + document.write
// viene bloccato silenziosamente (la finestra resta "about:blank").
function openHtmlInNewTab(html: string) {
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const w = window.open(url, '_blank')
  if (!w) {
    URL.revokeObjectURL(url)
    alert(
      'La nuova finestra è stata bloccata. Abilita i popup per questo sito e riprova.',
    )
    return
  }
  // Revoca dopo che la finestra ha sicuramente caricato (script auto-stampa
  // dentro l'HTML si è già attivato)
  setTimeout(() => URL.revokeObjectURL(url), 30_000)
}

function printSvg(svg: SVGSVGElement | null, layout: Layout, label: string) {
  if (!svg) return
  const svgString = buildExportSvgString(svg, layout)
  const html = `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="UTF-8">
<title>${label} — stampa 1:1</title>
<style>
  @page { size: ${layout.widthCm.toFixed(2)}cm ${layout.heightCm.toFixed(2)}cm; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; }
  body { display: flex; align-items: center; justify-content: center; }
  svg { display: block; }
  @media screen {
    body { padding: 1rem; font-family: system-ui, sans-serif; color: #444; }
    .info { max-width: 700px; margin: 0 auto 1rem; }
    .info h1 { font-size: 1.1rem; margin: 0 0 0.4rem; }
    .info p { margin: 0; font-size: 0.85rem; }
    .info button { margin-top: 0.6rem; padding: 0.5rem 1rem; cursor: pointer; }
  }
  @media print { .info { display: none; } }
</style>
</head>
<body>
<div class="info">
  <h1>${label}</h1>
  <p>Pagina dimensione ${layout.widthCm.toFixed(1)} × ${layout.heightCm.toFixed(1)} cm. Nel dialogo di stampa imposta margini "Nessuno" e ridimensionamento "Nessuno / 100%" per ottenere la scala 1:1.</p>
  <button onclick="window.print()">Stampa</button>
</div>
${svgString}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},300);});</script>
</body>
</html>`
  openHtmlInNewTab(html)
}

// Stampa il pezzo su più fogli A4 (portrait) con segni di taglio e label.
// Le pagine assemblate riproducono il cartamodello in scala 1:1.
function printSvgTiledA4(svg: SVGSVGElement | null, layout: Layout, label: string) {
  if (!svg) return
  const PAGE_W = 21.0
  const PAGE_H = 29.7
  const MARGIN = 1.0
  const PRINT_W = PAGE_W - 2 * MARGIN
  const PRINT_H = PAGE_H - 2 * MARGIN

  const cols = Math.max(1, Math.ceil(layout.widthCm / PRINT_W))
  const rows = Math.max(1, Math.ceil(layout.heightCm / PRINT_H))
  const total = cols * rows

  const tilesHtml: string[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const tileX = layout.minX + c * PRINT_W
      const tileY = layout.minY + r * PRINT_H
      const tileSvg = buildTileSvgString(svg, tileX, tileY, PRINT_W, PRINT_H)
      const code = `${String.fromCharCode(65 + r)}${c + 1}`
      const seq = r * cols + c + 1
      tilesHtml.push(`
        <div class="tile">
          <div class="tile-info">
            <strong>${code}</strong>
            <span>${label}</span>
            <span class="seq">${seq}/${total}</span>
            <span class="grid">riga ${r + 1}/${rows} · col ${c + 1}/${cols}</span>
          </div>
          <div class="crop tl"></div>
          <div class="crop tr"></div>
          <div class="crop bl"></div>
          <div class="crop br"></div>
          <div class="tile-svg-wrap">${tileSvg}</div>
        </div>
      `)
    }
  }

  const html = `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="UTF-8">
<title>${label} — A4 (${total} fogli)</title>
<style>
  @page { size: A4 portrait; margin: 0; }
  html, body { margin: 0; padding: 0; background: white; color: #222; font-family: system-ui, sans-serif; }
  .tile {
    position: relative;
    width: 21cm; height: 29.7cm;
    box-sizing: border-box;
    padding: ${MARGIN}cm;
    page-break-after: always;
    overflow: hidden;
  }
  .tile:last-child { page-break-after: auto; }
  .tile-svg-wrap { width: ${PRINT_W}cm; height: ${PRINT_H}cm; }
  .tile-svg-wrap svg { display: block; width: ${PRINT_W}cm; height: ${PRINT_H}cm; }
  .tile-info {
    position: absolute;
    top: 0.2cm; left: 0.2cm; right: 0.2cm;
    display: flex; gap: 0.5cm; align-items: baseline;
    font-size: 9pt;
    color: #555;
  }
  .tile-info strong {
    color: black;
    font-family: ui-monospace, monospace;
    font-size: 14pt;
    letter-spacing: 0.05em;
  }
  .tile-info .seq { margin-left: auto; font-family: ui-monospace, monospace; }
  .tile-info .grid { font-family: ui-monospace, monospace; }
  .crop {
    position: absolute;
    width: 0.5cm; height: 0.5cm;
    border-color: black;
    border-style: solid;
    border-width: 0;
  }
  .crop.tl { top: ${MARGIN - 0.25}cm; left: ${MARGIN - 0.25}cm; border-top-width: 0.5pt; border-left-width: 0.5pt; }
  .crop.tr { top: ${MARGIN - 0.25}cm; right: ${MARGIN - 0.25}cm; border-top-width: 0.5pt; border-right-width: 0.5pt; }
  .crop.bl { bottom: ${MARGIN - 0.25}cm; left: ${MARGIN - 0.25}cm; border-bottom-width: 0.5pt; border-left-width: 0.5pt; }
  .crop.br { bottom: ${MARGIN - 0.25}cm; right: ${MARGIN - 0.25}cm; border-bottom-width: 0.5pt; border-right-width: 0.5pt; }

  @media screen {
    body { padding: 1rem; }
    .info {
      max-width: 700px; margin: 0 auto 1.5rem;
      padding: 1rem; border: 1px solid #ddd; border-radius: 6px; background: #fafafa;
    }
    .info h1 { font-size: 1.1rem; margin: 0 0 0.4rem; }
    .info p { margin: 0.4rem 0; font-size: 0.9rem; line-height: 1.5; }
    .info button { margin-top: 0.6rem; padding: 0.5rem 1rem; cursor: pointer; font-size: 0.95rem; }
    .tile {
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
      margin: 0 auto 1.5rem;
      page-break-after: auto;
    }
  }
  @media print { .info { display: none; } body { padding: 0; } .tile { box-shadow: none; margin: 0; } }
</style>
</head>
<body>
<div class="info">
  <h1>${label} — stampa A4 ${cols}×${rows} fogli</h1>
  <p>Pagine totali: <strong>${total}</strong>. Ogni pagina copre ${PRINT_W}×${PRINT_H} cm. Margini ${MARGIN} cm per il taglio.</p>
  <p>Nel dialogo di stampa: imposta margini <strong>Nessuno</strong> e ridimensionamento <strong>100% / Nessuno</strong>. Dopo la stampa, allinea le pagine seguendo i codici <code>A1, A2, …</code> (lettera = riga, numero = colonna).</p>
  <button onclick="window.print()">Stampa</button>
</div>
${tilesHtml.join('\n')}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print();},400);});</script>
</body>
</html>`
  openHtmlInNewTab(html)
}
